import UserService from '../Services/UserService';
import Permissions from '../Utils/Permissions';

// Single source of truth for the app's navigation.
//
// Every navigable destination lives here exactly once. The offcanvas menu,
// the command palette (Cmd/Ctrl+K), and the keyboard-shortcut hook all derive
// their lists from this config so there is no duplicated nav markup to keep in
// sync. To add a page, add one entry here.
//
// Item shape:
//   path      — route path (required)
//   label     — display name (required)
//   icon      — Font Awesome class, e.g. 'fa-home' (required)
//   bg/color  — icon chip background / foreground colours
//   module    — optional permission gate (UserService.canAccessModule)
//   shortcut  — optional single key used with the modifier (Cmd/Ctrl)
//   keywords  — optional extra search terms for the command palette
//   badge     — optional live count shown next to the label (see Header)
//
// Section shape:
//   id          — stable key, also what the collapse state is stored under
//   label       — the heading. EVERY section has one: a group of links with no
//                 name is a group nobody can describe to a colleague.
//   defaultOpen — open until the user collapses it (their choice is
//                 remembered either way). The daily-work groups are open; the
//                 ones somebody visits a few times a month are not, so the
//                 menu opens on what the day is actually spent in.
//
// "Frequent" is not here: it is built at runtime from the pages this user
// actually opens (Hooks/useRecentPages), and every page in it also has its
// permanent home in one of the groups below.
export const NAV_SECTIONS = [
  {
    id: 'overview',
    label: 'Overview',
    defaultOpen: true,
    items: [
      {
        path: '/home', label: 'Home', icon: 'fa-home', bg: '#eff6ff', color: '#3b82f6', shortcut: 'h',
      },
    ],
  },
  {
    id: 'order-management',
    label: 'Order Management',
    defaultOpen: true,
    items: [
      {
        path: '/orders', label: 'Orders', icon: 'fa-shopping-cart', bg: '#f5f3ff', color: '#8b5cf6', shortcut: 'o', keywords: 'bookings',
      },
      {
        // Beside Orders because it is the desk's other all-day screen and the
        // mail is about those bookings. No `module`: a login and nothing more,
        // like Orders itself — see the note on the route in Routes.jsx.
        path: '/customer-service',
        label: 'Customer Service',
        icon: 'fa-inbox',
        bg: '#fffbeb',
        color: '#f59e0b',
        keywords: 'email inbox correspondence conversations replies awaiting unanswered desk mail support',
      },
      {
        path: '/service-offers', label: 'Service Offers', icon: 'fa-file-invoice', bg: '#fdf4ff', color: '#a855f7', keywords: 'quotes',
      },
      {
        path: '/trip-claims',
        label: 'Trip Claims',
        icon: 'fa-receipt',
        bg: '#fffbeb',
        color: '#d97706',
        module: 'CanInteractWithOperatorsScreen',
        keywords: 'receipts overtime expenses review approve',
        badge: 'tripClaims',
      },
      {
        path: '/trips', label: 'Daily Overview', icon: 'fa-route', bg: '#fff7ed', color: '#f97316', shortcut: 'j', keywords: 'trips schedule day',
      },
      {
        path: '/stats', label: 'Stats', icon: 'fa-chart-bar', bg: '#f0fdf4', color: '#22c55e', shortcut: 's',
      },
      {
        path: '/events', label: 'Events', icon: 'fa-calendar-alt', bg: '#fefce8', color: '#eab308',
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    items: [
      {
        path: '/experiences', label: 'Experiences', icon: 'fa-map-marked-alt', bg: '#f0fdfa', color: '#14b8a6',
      },
      {
        path: '/booking-services', label: 'Booking Extras', icon: 'fa-gift', bg: '#fdf2f8', color: '#ec4899', module: 'CanManageBookingServices', keywords: 'extras add-ons',
      },
      {
        path: '/partners', label: 'Partners', icon: 'fa-handshake', bg: '#eff6ff', color: '#3b82f6', module: 'CanInteractWithPartnersScreen',
      },
      {
        path: '/operators', label: 'Operators', icon: 'fa-users', bg: '#f8fafc', color: '#64748b', module: 'CanInteractWithOperatorsScreen', keywords: 'drivers',
      },
      {
        path: '/pricing', label: 'Pricing Engine', icon: 'fa-calculator', bg: '#f0fdf4', color: '#16a34a', module: 'CanInteractWithPricingEngine',
      },
      {
        path: '/discount-codes', label: 'Discount Codes', icon: 'fa-tags', bg: '#fff1f2', color: '#f43f5e', module: 'CanManageDiscountCodes', keywords: 'promo coupons',
      },
      {
        path: '/visitor-tracking', label: 'Visitor Tracking', icon: 'fa-crosshairs', bg: '#fdf4ff', color: '#a855f7', keywords: 'analytics',
      },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    items: [
      {
        path: '/payments', label: 'Payments', icon: 'fa-money-check-alt', bg: '#fef2f2', color: '#dc2626', module: 'CanInteractWithPayments',
      },
      {
        path: '/invoicing', label: 'Invoicing', icon: 'fa-file-invoice-dollar', bg: '#eff6ff', color: '#2563eb', keywords: 'invoices billing',
      },
      {
        path: '/accounts', label: 'Accounts', icon: 'fa-piggy-bank', bg: '#fff7ed', color: '#d97706',
      },
      {
        path: '/month-end', label: 'Month End', icon: 'fa-calendar-check', bg: '#ecfdf5', color: '#10b981', module: 'CanInteractWithMonthEnd', keywords: 'closing reconciliation',
      },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    items: [
      {
        path: '/partner-sync', label: 'Sync', icon: 'fa-sync', bg: '#eff6ff', color: '#3b82f6', shortcut: 'y', keywords: 'partner sync',
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    items: [
      {
        path: '/notification-settings', label: 'Notification Settings', icon: 'fa-bell', bg: '#fff7ed', color: '#f97316',
      },
      {
        path: '/notification-groups', label: 'Notification Groups', icon: 'fa-users-cog', bg: '#f8fafc', color: '#64748b', module: 'CanManageNotificationGroups',
      },
      {
        path: '/message-presets',
        label: 'Saved Messages',
        icon: 'fa-comment-dots',
        bg: '#ecfeff',
        color: '#0891b2',
        keywords: 'presets passenger whatsapp email templates canned replies composer chips snippets',
      },
      {
        // One nav point for the whole AI Admin area; its sections
        // (Knowledge Bases, Agent Instructions) are chosen inside the page,
        // because this nav is two levels only — section headers and flat
        // items, never nested children. Keywords carry the section names so
        // the command palette still finds this by "knowledge base" or
        // "instructions", which is what people type.
        path: '/ai-admin',
        label: 'AI Admin',
        icon: 'fa-wand-magic-sparkles',
        bg: '#f5f3ff',
        color: '#7c3aed',
        module: Permissions.MANAGE_AI,
        keywords: 'ai admin assistant knowledge bases documents rag retrieval handbook prompts instructions guidance agent publish draft model settings',
      },
    ],
  },
];

// Sections with permission-gated items removed; empty sections dropped.
export function getAccessibleSections() {
  const user = UserService.getInstance();
  return NAV_SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.module || user.canAccessModule(item.module),
      ),
    }))
    .filter((section) => section.items.length > 0);
}

// Flat list of every destination the current user can reach.
export function getAccessibleItems() {
  return getAccessibleSections().flatMap((section) => section.items);
}

// Look up a single item by its route path (unfiltered).
export function findNavItem(path) {
  return NAV_SECTIONS
    .flatMap((section) => section.items)
    .find((item) => item.path === path) || null;
}
